/**
 * 영역 가드 — 사용자(/app)·운영자(/admin). 세션도 영역별로 따로다 (260917-admin-area-split ·
 * 260917-realm-sessions-impersonation · 261013-account-brand-membership). 공개 화면은 레이아웃 밖(root 직계)이라 내 계정을 묻지 않는다.
 *
 * ★ 가드의 이동은 전부 replace — push 면 뒤로가기가 가드에 다시 걸려 갇힌다.
 * ★ `<Navigate>` 를 쓰지 않는다 — 위치를 구독하는 레이아웃에서 동기 무한 루프가 된다(components/Redirect.tsx).
 * ★ me 오류가 401 이 아니면(500·네트워크) 로그인으로 보내지 않는다 — 로그인 ↔ 영역을 오가는 반복이 된다.
 * 역할 검사는 두지 않는다: 운영자 쿠키는 운영자 영역 요청에서만, 사용자 쿠키는 사용자 영역 요청에서만 읽히고,
 * 서버가 역할과 영역을 대조한다. 강제는 서버(계약 access)가 한다.
 */
import { Outlet, useLocation } from '@tanstack/react-router';
import { AdminShell } from '../components/AdminShell.tsx';
import { AppShell } from '../components/AppShell.tsx';
import { ImpersonationEnded } from '../components/ImpersonationBanner.tsx';
import { Redirect } from '../components/Redirect.tsx';
import { ErrorBox, Loading } from '../components/ui/states.tsx';
import { ApiError } from '../lib/call.ts';
import { impersonation } from '../lib/impersonation.ts';
import { useMe } from '../lib/me.ts';
import { PATHS } from '../lib/paths.ts';
import { PaymentPending } from './billing.tsx';
import { NoBrand } from './no-brand.tsx';
import { PendingApproval } from './pending.tsx';

const isUnauthorized = (e: unknown) => e instanceof ApiError && e.status === 401;

export function AppLayout() {
  const me = useMe();
  const location = useLocation();
  if (me.isLoading) return <Loading />;
  if (me.error && !isUnauthorized(me.error)) return <FatalError error={me.error} />;
  // 대리 탭은 로그인 화면으로 보내지 않는다 — 그 브라우저의 다른 신원으로 이어진다
  if (!me.data)
    return impersonation() ? (
      <ImpersonationEnded />
    ) : (
      <Redirect key="login" go={(n) => n({ to: '/login', replace: true })} />
    );
  // 대리 중에는 대상의 화면을 그대로 보여준다 — 비밀번호 변경은 서버도 막는다
  if (me.data.mustChangePassword && !me.data.impersonator && location.pathname !== PATHS.app.password)
    return <Redirect key="password" go={(n) => n({ to: '/app/account/password', replace: true })} />;
  // 대행사 직원은 브랜드가 없거나 승인 대기뿐이어도 일반 화면이다(261025-agency-white-label) — 서버가 조회를 열고 빈 결과를 준다
  const staffShell =
    me.data.agencyRole === 'staff' && (me.data.brandStatus === 'pending' || me.data.brandStatus === 'none');
  if (staffShell)
    return (
      <AppShell me={me.data}>
        <Outlet />
      </AppShell>
    );
  // 승인 전에는 틀을 그리지 않는다 — 서버도 me·logout·password·withdraw 외에는 403 이다
  if (me.data.brandStatus === 'pending') return <PendingApproval me={me.data} />;
  // 결제 대기 — 구독을 시작하는 화면만. 서버도 결제 계약 밖은 403 이다 (261011-subscription-billing-payup)
  if (me.data.brandStatus === 'confirmed') return <PaymentPending me={me.data} />;
  // 브랜드가 하나도 없다(모두 지워졌다 — 계약 종료) — 브랜드 추가만 (261022-brand-delete-is-contract-end)
  if (me.data.brandStatus === 'none') return <NoBrand me={me.data} />;
  return (
    <AppShell me={me.data}>
      <Outlet />
    </AppShell>
  );
}

export function AdminLayout() {
  const me = useMe();
  const location = useLocation();
  if (me.isLoading) return <Loading />;
  if (me.error && !isUnauthorized(me.error)) return <FatalError error={me.error} />;
  if (!me.data) return <Redirect key="login" go={(n) => n({ to: '/admin/login', replace: true })} />;
  if (me.data.mustChangePassword && location.pathname !== PATHS.admin.password)
    return <Redirect key="password" go={(n) => n({ to: '/admin/account/password', replace: true })} />;
  return (
    <AdminShell me={me.data}>
      <Outlet />
    </AdminShell>
  );
}

function FatalError({ error }: { error: unknown }) {
  return (
    <div className="mx-auto max-w-md p-6">
      <ErrorBox error={error} />
    </div>
  );
}
